import clsx from 'clsx';
import { Property } from 'csstype';
import {
  type ChangeEvent,
  forwardRef,
  useImperativeHandle,
  useRef,
} from 'react';
import { useIntl } from 'react-intl';

import { ResizeIcon } from '@dynatrace/strato-icons';

import * as styles from './TextArea.sty.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useControllableState } from '../../core/hooks/useControllableState.js';
import { useFocusRing } from '../../core/hooks/useFocusRing.js';
import { useFormControlState } from '../../core/hooks/useFormControlState.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import { DataTestId } from '../../core/types/data-props.js';
import type { FocusEvents, KeyboardEvents } from '../../core/types/events.js';
import type { FormControlProps } from '../../core/types/form-control-props.js';
import { MaskingProps } from '../../core/types/masking-props.js';
import { StylingProps } from '../../core/types/styling-props.js';
import { mergeProps as _mergeProps } from '../../core/utils/merge-props.js';
import { Flex } from '../../layouts/flex/Flex.js';
import { withFormFieldMessageContextProvider } from '../forms-core/components/form-field/FormFieldMessageContextProvider.js';
import { InternalBaseItem as BaseItem } from '../forms-core/components/form-field-messages/FormFieldMessageBaseItem.js';
import { useFormFieldMessagesContext } from '../forms-core/hooks/useFormFieldMessagesContext.js';
import {
  getI18nValidationState,
  useFormFieldValidation,
} from '../forms-core/hooks/useFormFieldValidation.js';
import { useSyncedFormControlProps } from '../forms-core/hooks/useSyncedFormControlProps.js';
import { FormFieldMessagesAction } from '../forms-core/state/formfield-messages-state-reducer.js';
import { useInputHint } from '../hooks/useInputHint.js';
import type { FormControlRef } from '../shared-types.js';
import { useCustomMessageEmitter } from '../validation/useCustomMessageEmitter.js';
import { useValidationStateEmitter } from '../validation/useValidationStateEmitter.js';

const COMPONENT_NAME = 'TextArea';

// #region Typings
/**
 * @public
 */
export type TextAreaProps = FormControlProps<
  string,
  (value: string, event: ChangeEvent<HTMLTextAreaElement>) => void | never
> &
  FocusEvents &
  StylingProps &
  DataTestId &
  MaskingProps &
  KeyboardEvents &
  BehaviorTrackingProps & {
    placeholder?: string;
    /**
     * This attribute indicates whether the textarea is resizable by the user, and, if so, in which direction.
     * @defaultValue 'both'
     */
    resize?: 'both' | 'vertical' | 'horizontal' | 'none';
    /**
     * The visible width of the text control, in average character widths.
     * If it is specified, it must be a positive integer.
     * @defaultValue 20
     */
    cols?: number;
    /**
     * The number of visible text lines for the control. If it is specified, it must be a positive integer.
     * @defaultValue 2
     */
    rows?: number;
    /**
     * The minimum number of characters required that the user should enter.
     * Doesn't stop the user from removing characters so that the number entered goes past the minimum,
     * but it does make the value entered into the <textarea> invalid.
     * With a set minlength an empty <textarea> is still considered valid unless you also have the required attribute set.
     */
    minLength?: number;
    /**
     * The maximum number of characters that the user can enter. If this value isn't specified,
     * the user can enter an unlimited number of characters.
     */
    maxLength?: number;
    /**
     * This attribute indicates whether the value of the control can be automatically completed by the browser.
     * If no autocompletion is desired, it can be set to "off".
     */
    autoComplete?: string;
    /**
     * Sets the width of the textarea element.
     * @defaultValue 'full'
     */
    width?: 'content' | 'full' | Property.Width<string & Record<string, never>>;
  };

/**
 * @public
 */
export type TextAreaInputRef = Omit<
  FormControlRef<HTMLDivElement>,
  'inputRef'
> & {
  readonly inputRef?: HTMLTextAreaElement | null;
};
// #endregion

const TextAreaRoot = /* @__PURE__ */ forwardRef<
  TextAreaInputRef,
  TextAreaProps
>((props, forwardedRef) => {
  const intl = useIntl();

  const defaultPlaceholder = intl.formatMessage({
    defaultMessage: 'Add text',
    id: 'E7kKT3Td1FKB37wr',
    description: 'Default placeholder for the text area input.',
  });

  const {
    onChange,
    onBlur,
    onValidityChange,
    minLength,
    maxLength,
    id: propId,
    disabled: propDisabled,
    required: propRequired,
    readOnly: propReadOnly,
    resize = 'both',
    controlState,
    value: propValue,
    defaultValue,
    width = 'full',
    placeholder = defaultPlaceholder,
    'aria-describedby': ariaDescribedby,
    'aria-disabled': ariaDisabledProp,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    className: consumerClassName,
    style: consumerStyle,
    ...remainingProps
  }: TextAreaProps = props;

  const [behavioralTrackingProps, restProps] =
    useBehavioralTrackingProps(remainingProps);

  const { disabled, required, id, ariaDisabled, readOnly } =
    useSyncedFormControlProps({
      propDisabled,
      propRequired,
      propId,
      propAriaDisabled: ariaDisabledProp,
      propReadOnly,
    });
  const disabledOrAriaDisabled = disabled || ariaDisabled || false;

  const { state: messagesContextState, dispatch: messagesContextDispatch } =
    useFormFieldMessagesContext();

  // Imperative Handle Construction
  const containerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const _emitValidationState = useValidationStateEmitter(onValidityChange);
  const emitValidationState = useCustomMessageEmitter(
    _emitValidationState,
    () => getI18nValidationState(intl, textareaRef.current, 'manual'),
  );

  useImperativeHandle(forwardedRef, (): TextAreaInputRef => {
    return {
      element: containerRef.current,
      inputRef: textareaRef.current,
      focus() {
        textareaRef.current?.focus();
      },
      validate() {
        textareaRef.current?.checkValidity();
        messagesContextDispatch?.({
          type: FormFieldMessagesAction.SETISTOUCHED,
          payload: { isTouched: true },
        });
        emitValidationState(
          getI18nValidationState(intl, textareaRef.current, 'manual'),
        );
      },
      // DEPRECATION: [APPDEV-18178] BREAKING-CHANGE: Remove validateAsync() and change validate() to return Promise<ValidationState>.
      validateAsync: async () => {
        textareaRef.current?.checkValidity();
        messagesContextDispatch?.({
          type: FormFieldMessagesAction.SETISTOUCHED,
          payload: { isTouched: true },
        });
        return emitValidationState(
          getI18nValidationState(intl, textareaRef.current, 'manual'),
        );
      },
    };
  });

  const [controlStateHasError, hint] = useFormControlState(controlState);
  const hasError = controlState
    ? controlStateHasError
    : messagesContextState.showError;

  const [value, setValue] = useControllableState({
    defaultValue,
    value: propValue,
  });

  const {
    onChange: validationOnChange,
    onBlur: validationOnBlur,
    onInvalid: validationOnInvalid,
    ariaProps,
  } = useFormFieldValidation(
    textareaRef,
    { required, minLength, maxLength, value: value ?? '' },
    undefined,
    undefined,
    _emitValidationState,
  );

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = event.target?.value;
    setValue(newValue);
    onChange?.(newValue, event);
    !controlState && validationOnChange();
  };

  // _useInputHint will be replaced with new validation concept. APPDEV-8298
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  const { inputProps, hintProps } = useInputHint({
    ariaDescribedby,
    hint,
    hasError,
  });

  const {
    focusProps: { onFocus, onBlur: focusRingOnBlur },
    focusClassName,
  } = useFocusRing({
    color: hasError ? 'critical' : 'neutral',
    ignoreModality: true,
    disabled,
  });

  const styleVariants = {
    error: hasError,
    disabled: disabledOrAriaDisabled,
    readOnly,
  };

  return (
    <Flex
      gap={4}
      flexDirection="column"
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      ref={containerRef}
      className={consumerClassName}
      style={consumerStyle}
      {...behavioralTrackingProps}
      data-dt-component={COMPONENT_NAME}
    >
      <div
        className={styles.container({
          contentWidth: width === 'content',
          resize,
        })}
        style={{ width }}
      >
        <textarea
          ref={textareaRef}
          id={id}
          minLength={minLength}
          maxLength={maxLength}
          className={clsx(styles.textArea(styleVariants), focusClassName)}
          disabled={disabled}
          required={required}
          readOnly={readOnly}
          value={value}
          aria-required={required}
          aria-disabled={ariaDisabled}
          placeholder={placeholder}
          onChange={handleChange}
          onInvalid={(e) => !controlState && validationOnInvalid(e)}
          onFocus={onFocus}
          onBlur={(e) => {
            !controlState && validationOnBlur(e);
            focusRingOnBlur(e);
            onBlur?.(e);
          }}
          {..._mergeProps(restProps, controlState ? inputProps : ariaProps)}
        />
        {resize !== 'none' && (
          <ResizeIcon className={styles.resizeIcon(styleVariants)} />
        )}
      </div>
      {hint && (
        <BaseItem
          variant={hasError ? 'error' : 'hint'}
          disabled={disabled || ariaDisabled}
          {...hintProps}
        >
          {hint}
        </BaseItem>
      )}
    </Flex>
  );
});

/**
 * The `TextArea` component can be used as a multi-line plain-text editing control to
 * enter free-form text, such as a comment on a form.
 * The component uses a fallback FormFieldMessages context if it wasn't wrapped in a FormField.
 * @public
 */
export const TextArea = withFormFieldMessageContextProvider<
  TextAreaProps,
  TextAreaInputRef
>(TextAreaRoot);

(TextArea as typeof TextArea & { displayName: string }).displayName =
  COMPONENT_NAME;
